import { defineStore } from 'pinia'
import { computed, reactive, ref, shallowRef } from 'vue'
import { useCameraStore } from '@/store/camera'
import {
  getMonitorMode,
  requiresMainCameraMonitor,
  requiresSessionMonitor
} from '@/store/monitorSessionUtils'
import { useUserStore } from '@/store/user'
import {
  type SlaveMonitorQrPayload,
  buildSlaveMonitorQrValue,
  getMonitorPositionConfig
} from '@/store/monitorSessionUtils'
import { useScreenShareTest } from '@/views/newDetect/hooks/useScreenShareTest'
import { useTencentDeviceTest } from '@/views/newDetect/hooks/useTencentDeviceTest'
import { store } from './index'
import type {
  DeviceDebugPageStatus,
  DeviceDebugPaperConfig,
  DeviceDebugStatus,
  DeviceModuleState,
  DeviceOverviewItem,
  DeviceRequirementResult
} from '@/views/newDetect/types'

const ENABLE_WEB_SLAVE_MONITOR = true

const MODULE_LABELS = {
  camera: '主机位',
  audio: '音频',
  qr: '侧机位',
  screen: '屏幕共享'
} as const

function createModuleState(
  enabled = false,
  status: DeviceDebugStatus = 'disabled'
): DeviceModuleState {
  return {
    enabled,
    status,
    message: enabled ? '待检测' : '当前问卷未启用'
  }
}

function updateModuleState(
  target: DeviceModuleState,
  payload: Partial<DeviceModuleState> & Pick<DeviceModuleState, 'status' | 'message'>
) {
  target.status = payload.status
  target.message = payload.message
  target.lastCheckedAt = payload.lastCheckedAt || Date.now()
  if (typeof payload.enabled === 'boolean') {
    target.enabled = payload.enabled
  }
}

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

function hasLiveTrack(stream: MediaStream | undefined, kind: 'video' | 'audio') {
  const track = kind === 'video' ? stream?.getVideoTracks?.()[0] : stream?.getAudioTracks?.()[0]
  return !!track && track.readyState === 'live'
}

function isSamePaperConfig(
  currentPaper: DeviceDebugPaperConfig | null,
  nextPaper?: DeviceDebugPaperConfig | null
) {
  if (!currentPaper || !nextPaper) return false

  const currentMonitorRoom = currentPaper.monitorRoomList?.[0]
  const nextMonitorRoom = nextPaper.monitorRoomList?.[0]

  return (
    currentPaper.paperId === nextPaper.paperId &&
    currentPaper.actiLinkId === nextPaper.actiLinkId &&
    currentPaper.startDateTime === nextPaper.startDateTime &&
    currentPaper.isOpenMasterMonitor === nextPaper.isOpenMasterMonitor &&
    currentPaper.isOpenSlaveMonitor === nextPaper.isOpenSlaveMonitor &&
    currentPaper.isOpenDeskMonitor === nextPaper.isOpenDeskMonitor &&
    currentPaper.ocIsImageMonitor === nextPaper.ocIsImageMonitor &&
    currentMonitorRoom?.roomGroupId === nextMonitorRoom?.roomGroupId &&
    currentMonitorRoom?.positions === nextMonitorRoom?.positions &&
    currentMonitorRoom?.roomCodes === nextMonitorRoom?.roomCodes &&
    currentMonitorRoom?.videoUserIds === nextMonitorRoom?.videoUserIds
  )
}

export const useDeviceDebugStore = defineStore(
  'APP_DEVICE_DEBUG',
  () => {
    const userStore = useUserStore()
    const currentPaper = ref<DeviceDebugPaperConfig | null>(null)
    const camera = reactive<DeviceModuleState>(createModuleState())
    const audio = reactive<DeviceModuleState>(createModuleState())
    const qr = reactive<DeviceModuleState>(createModuleState())
    const screen = reactive<DeviceModuleState>(createModuleState())

    const cameraStream = shallowRef<MediaStream>()
    const screenStream = shallowRef<MediaStream>()
    const previewVideoElement = shallowRef<HTMLVideoElement | null>(null)
    const cameraDevices = ref<MediaDeviceInfo[]>([])
    const selectedCameraId = ref('')
    const selectedCameraLabel = ref('')
    const micLevel = ref(0)
    const micPassed = ref(false)
    const speakerPassed = ref(false)
    const qrValue = ref('normstar://new-detect/placeholder')
    const screenSourceName = ref('')
    const activityInitialized = ref(false)
    const debugPageInitialized = ref(false)

    const tencentDeviceTest = useTencentDeviceTest()
    const screenShareTest = useScreenShareTest()

    const enabledModules = computed(() => {
      return {
        camera: camera.enabled,
        audio: audio.enabled,
        qr: qr.enabled,
        screen: screen.enabled
      }
    })

    const overviewItems = computed<DeviceOverviewItem[]>(() => {
      return [
        { key: 'camera', label: MODULE_LABELS.camera, ...camera },
        { key: 'audio', label: MODULE_LABELS.audio, ...audio },
        { key: 'qr', label: MODULE_LABELS.qr, ...qr },
        { key: 'screen', label: MODULE_LABELS.screen, ...screen }
      ]
    })

    const pageStatus = computed<DeviceDebugPageStatus>(() => {
      const states = overviewItems.value.filter((item) => item.enabled)

      if (states.length === 0) {
        return 'idle'
      }

      if (states.some((item) => item.status === 'testing')) {
        return 'testing'
      }

      if (states.some((item) => item.status === 'error')) {
        return 'failed'
      }

      if (states.every((item) => item.status === 'success' || item.status === 'disabled')) {
        return 'ready'
      }

      return 'idle'
    })

    const isAllPassed = computed(() => {
      return overviewItems.value
        .filter((item) => item.enabled)
        .every((item) => item.status === 'success' || item.status === 'disabled')
    })

    const applyModuleEnabledState = (target: DeviceModuleState, enabled: boolean) => {
      target.enabled = enabled
      target.status = enabled ? 'idle' : 'disabled'
      target.message = enabled ? '待检测' : '当前问卷未启用'
      target.lastCheckedAt = Date.now()
    }

    const buildSlaveQrPayload = (paper: DeviceDebugPaperConfig): SlaveMonitorQrPayload | null => {
      const monitorRoom = paper.monitorRoomList?.[0]
      const slaveRoomConfig = getMonitorPositionConfig(monitorRoom, 'slave')
      const key = userStore.userInfo?.uuid

      if (!monitorRoom || !slaveRoomConfig || !key) {
        return null
      }

      return {
        actiId: paper.actiId,
        actiLinkId: paper.actiLinkId,
        paperId: paper.paperId,
        accountId: monitorRoom.accountId || userStore.accountId || '',
        roomGroupId: monitorRoom.roomGroupId,
        roomCode: slaveRoomConfig.roomCode,
        userId: slaveRoomConfig.userId,
        startDateTime: paper.startDateTime,
        key
      }
    }

    const syncSlaveQrValue = (paper: DeviceDebugPaperConfig) => {
      const payload = buildSlaveQrPayload(paper)

      if (!payload) {
        qrValue.value = 'normstar://monitor/slave/invalid'
        return false
      }

      qrValue.value = buildSlaveMonitorQrValue(payload)
      return true
    }

    const syncCameraMeta = async () => {
      const devices = await tencentDeviceTest.getCameraDevices()
      cameraDevices.value = devices

      if (!selectedCameraId.value) {
        selectedCameraId.value = devices[0]?.deviceId || ''
      }

      selectedCameraLabel.value =
        devices.find((item) => item.deviceId === selectedCameraId.value)?.label || ''
    }

    const initFromPaperConfig = (paper?: DeviceDebugPaperConfig | null) => {
      if (isSamePaperConfig(currentPaper.value, paper)) {
        return
      }

      currentPaper.value = paper || null
      activityInitialized.value = false
      debugPageInitialized.value = false

      if (!paper) {
        applyModuleEnabledState(camera, false)
        applyModuleEnabledState(audio, false)
        applyModuleEnabledState(qr, false)
        applyModuleEnabledState(screen, false)
        return
      }

      const needMainCamera = requiresMainCameraMonitor(paper)
      const needSessionMonitor = getMonitorMode(paper) === 'session-monitor'

      applyModuleEnabledState(camera, needMainCamera)
      applyModuleEnabledState(audio, needMainCamera)
      applyModuleEnabledState(
        qr,
        needSessionMonitor && ENABLE_WEB_SLAVE_MONITOR && !!paper.isOpenSlaveMonitor
      )
      applyModuleEnabledState(screen, needSessionMonitor && !!paper.isOpenDeskMonitor)

      if (paper.isOpenSlaveMonitor) {
        syncSlaveQrValue(paper)
      } else {
        qrValue.value = 'normstar://monitor/slave/disabled'
      }
      screenSourceName.value = ''
      micLevel.value = 0
      micPassed.value = false
      speakerPassed.value = false

      if (!screen.enabled) {
        stopScreenShareCheck()
      }
    }

    const checkAudio = async () => {
      if (!audio.enabled) {
        updateModuleState(audio, {
          status: 'disabled',
          message: '当前问卷未启用'
        })
        return
      }

      updateModuleState(audio, {
        status: 'testing',
        message: '正在检测麦克风权限'
      })

      try {
        await tencentDeviceTest.startAudioDiagnostics({
          stream: cameraStream.value,
          onLevelChange: (level) => {
            micLevel.value = level
          }
        })

        micPassed.value = true
        updateModuleState(audio, {
          status: 'success',
          message: '麦克风检测正常'
        })
      } catch (error) {
        micPassed.value = false
        updateModuleState(audio, {
          status: 'error',
          message: extractErrorMessage(error, '麦克风检测失败')
        })
      }
    }

    const checkCamera = async () => {
      if (!camera.enabled) {
        updateModuleState(camera, {
          status: 'disabled',
          message: '当前问卷未启用'
        })
        return
      }

      updateModuleState(camera, {
        status: 'testing',
        message: '正在获取摄像头画面'
      })

      try {
        const result = await tencentDeviceTest.ensureCameraReady(selectedCameraId.value)
        cameraStream.value = result.stream
        cameraDevices.value = result.devices
        selectedCameraId.value = result.selectedDeviceId
        selectedCameraLabel.value =
          result.devices.find((item) => item.deviceId === result.selectedDeviceId)?.label || ''

        updateModuleState(camera, {
          status: 'success',
          message: '主摄像头连接正常'
        })

        await checkAudio()
      } catch (error) {
        updateModuleState(camera, {
          status: 'error',
          message: extractErrorMessage(error, '主摄像头检测失败')
        })

        if (audio.enabled) {
          updateModuleState(audio, {
            status: 'error',
            message: '音频检测未完成，请先修复摄像头权限'
          })
        }
      }
    }

    const changeCamera = async (deviceId: string) => {
      if (!camera.enabled) return

      updateModuleState(camera, {
        status: 'testing',
        message: '正在切换摄像头'
      })

      try {
        const result = await tencentDeviceTest.changeCamera(deviceId)
        cameraStream.value = result.stream
        cameraDevices.value = result.devices
        selectedCameraId.value = result.selectedDeviceId
        selectedCameraLabel.value =
          result.devices.find((item) => item.deviceId === result.selectedDeviceId)?.label || ''

        updateModuleState(camera, {
          status: 'success',
          message: '主摄像头切换成功'
        })

        await checkAudio()
      } catch (error) {
        updateModuleState(camera, {
          status: 'error',
          message: extractErrorMessage(error, '切换摄像头失败')
        })
      }
    }

    const playSpeakerTest = async () => {
      try {
        await tencentDeviceTest.playSpeakerTest()
      } catch (error) {
        updateModuleState(audio, {
          status: 'error',
          message: extractErrorMessage(error, '播放测试音失败，请检查扬声器设置')
        })
      }
    }

    const confirmSpeakerPassed = () => {
      speakerPassed.value = true
    }

    const initQrPlaceholder = () => {
      if (!qr.enabled) {
        updateModuleState(qr, {
          status: 'disabled',
          message: '当前问卷未启用'
        })
        return
      }

      const paper = currentPaper.value
      const hasQrPayload = paper ? syncSlaveQrValue(paper) : false

      if (!hasQrPayload) {
        updateModuleState(qr, {
          status: 'error',
          message: '侧机位二维码生成失败，请刷新页面后重试'
        })
        return
      }

      updateModuleState(qr, {
        status: 'success',
        message: '请使用小程序扫码待命，测评开始时间一到将自动入房'
      })
    }

    const startScreenShareCheck = async () => {
      if (!screen.enabled) {
        updateModuleState(screen, {
          status: 'disabled',
          message: '当前问卷未启用'
        })
        return
      }

      updateModuleState(screen, {
        status: 'testing',
        message: '正在请求屏幕共享权限'
      })

      try {
        const result = await screenShareTest.startScreenShare({
          onEnded: () => {
            screenStream.value = undefined
            screenSourceName.value = ''
            updateModuleState(screen, {
              status: 'error',
              message: '屏幕共享已中断，请重新开启'
            })
          }
        })

        screenStream.value = result.stream
        screenSourceName.value = result.sourceName
        updateModuleState(screen, {
          status: 'success',
          message: '屏幕共享检测正常'
        })
      } catch (error) {
        screenStream.value = undefined
        screenSourceName.value = ''
        updateModuleState(screen, {
          status: 'error',
          message: extractErrorMessage(error, '屏幕共享检测失败，请重新选择共享内容')
        })
      }
    }

    const stopScreenShareCheck = () => {
      screenShareTest.stopScreenShare()
      screenStream.value = undefined
      screenSourceName.value = ''

      if (screen.enabled) {
        updateModuleState(screen, {
          status: 'idle',
          message: '请在活动页或调试页重新开启屏幕共享'
        })
      }
    }

    const markScreenShareInterrupted = (
      message = '页面刷新后屏幕共享已中断，请重新开启屏幕共享'
    ) => {
      screenStream.value = undefined
      screenSourceName.value = ''

      if (screen.enabled) {
        updateModuleState(screen, {
          status: 'error',
          message
        })
      }
    }

    const initActivityChecks = async () => {
      if (!currentPaper.value) return

      activityInitialized.value = true
      initQrPlaceholder()
      await syncCameraMeta()

      if (camera.enabled) {
        await checkCamera()
      }

      if (screen.enabled && !hasLiveTrack(screenStream.value, 'video')) {
        updateModuleState(screen, {
          status: 'idle',
          message: '请在活动页点击“开启屏幕共享”完成授权'
        })
      }
    }

    const initDebugPageChecks = async () => {
      if (!currentPaper.value) return

      debugPageInitialized.value = true
      initQrPlaceholder()
      await syncCameraMeta()

      if (camera.enabled && camera.status !== 'success') {
        await checkCamera()
      } else if (audio.enabled && audio.status !== 'success') {
        await checkAudio()
      }

      if (screen.enabled && !hasLiveTrack(screenStream.value, 'video')) {
        updateModuleState(screen, {
          status: 'idle',
          message: '请手动重新检测屏幕共享'
        })
      }
    }

    const retryAllEnabledChecks = async () => {
      if (camera.enabled) {
        await checkCamera()
      }

      if (qr.enabled) {
        initQrPlaceholder()
      }

      if (screen.enabled) {
        await startScreenShareCheck()
      }
    }

    const syncSummaryToActivity = () => {
      ;[camera, audio, qr, screen].forEach((item) => {
        item.lastCheckedAt = Date.now()
      })
    }

    const bindPreviewVideoElement = (element?: HTMLVideoElement | null) => {
      previewVideoElement.value = element || null
    }

    const cleanupDebugPageResources = () => {
      tencentDeviceTest.cleanup()
      micLevel.value = 0
    }

    const cleanupAllResources = async () => {
      cleanupDebugPageResources()
      stopScreenShareCheck()
      cameraStream.value = undefined
      await useCameraStore()
        .stopCamera()
        .catch(() => undefined)
    }

    const resetState = async () => {
      initFromPaperConfig(null)
      cameraDevices.value = []
      selectedCameraId.value = ''
      selectedCameraLabel.value = ''
      micLevel.value = 0
      micPassed.value = false
      speakerPassed.value = false
      qrValue.value = 'normstar://new-detect/placeholder'
      screenSourceName.value = ''
      activityInitialized.value = false
      debugPageInitialized.value = false

      await cleanupAllResources().catch(() => undefined)
    }

    const getRequirementResult = (paper: DeviceDebugPaperConfig): DeviceRequirementResult => {
      if (
        requiresMainCameraMonitor(paper) &&
        (camera.status !== 'success' || !hasLiveTrack(cameraStream.value, 'video'))
      ) {
        return {
          passed: false,
          message: '主机位状态异常，请先完成设备调试'
        }
      }

      if (
        paper.isOpenMasterMonitor &&
        (audio.status !== 'success' || !hasLiveTrack(cameraStream.value, 'audio'))
      ) {
        return {
          passed: false,
          message: '音频状态异常，请先完成设备调试'
        }
      }

      if (paper.isOpenSlaveMonitor && qr.status !== 'success') {
        return {
          passed: false,
          message: '侧机位状态异常，请先完成设备调试'
        }
      }

      if (
        paper.isOpenDeskMonitor &&
        (screen.status !== 'success' || !hasLiveTrack(screenStream.value, 'video'))
      ) {
        return {
          passed: false,
          message: '屏幕共享未完成，请先在活动页或调试页完成检测'
        }
      }

      return {
        passed: true,
        message: ''
      }
    }

    return {
      currentPaper,
      camera,
      audio,
      qr,
      screen,
      cameraStream,
      screenStream,
      cameraDevices,
      selectedCameraId,
      selectedCameraLabel,
      micLevel,
      micPassed,
      speakerPassed,
      qrValue,
      screenSourceName,
      activityInitialized,
      debugPageInitialized,
      enabledModules,
      overviewItems,
      pageStatus,
      isAllPassed,
      initFromPaperConfig,
      initActivityChecks,
      initDebugPageChecks,
      checkCamera,
      changeCamera,
      checkAudio,
      playSpeakerTest,
      confirmSpeakerPassed,
      initQrPlaceholder,
      startScreenShareCheck,
      stopScreenShareCheck,
      markScreenShareInterrupted,
      retryAllEnabledChecks,
      syncSummaryToActivity,
      cleanupDebugPageResources,
      cleanupAllResources,
      resetState,
      getRequirementResult,
      previewVideoElement,
      bindPreviewVideoElement
    }
  },
  {
    persist: {
      paths: [
        'currentPaper',
        'camera',
        'audio',
        'qr',
        'screen',
        'selectedCameraId',
        'selectedCameraLabel',
        'micPassed',
        'speakerPassed',
        'qrValue',
        'screenSourceName'
      ]
    }
  }
)

export function useDeviceDebugStoreWithOut() {
  return useDeviceDebugStore(store)
}
